import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Flag,
  Pencil,
  Trash2,
  UserRound,
  Users,
} from 'lucide-react';
import type { Asociado } from '@lib/types';
import { COLUMNAS_VISIBLES } from '@lib/types';
import type { ObservacionTitular } from '@lib/services/padrones.service';
import { RenaperDetalleModal } from './RenaperDetalleModal';
import { EstadoLegajoBadge } from './EstadoLegajoBadge';

// Catálogo reducido por requerimiento de producto: titular, cónyuge, hijos
// y adherentes (otros adultos que el titular decida sumar al plan).
const PARENTESCO_LABEL: Record<string, string> = {
  '0': 'Titular',
  '5': 'Cónyuge',
  '7': 'Hijo/a',
  '13': 'Adherente',
};

interface Props {
  asociados: Asociado[];
  onEdit?: (a: Asociado) => void;
  /**
   * Si está definida, cada fila muestra un botón "Eliminar" para sacar al
   * asociado del padrón. Se llama con confirmación nativa simple. Solo se
   * pasa desde PadronDetalle cuando el padrón está en estado `borrador`.
   */
  onDelete?: (a: Asociado) => void;
  /**
   * Si el padrón está cerrado, pasar el mapa `legajoNumero → estadoLegajo`
   * para mostrar la columna "Proceso" con el estado del onboarding por legajo.
   * Cuando se omite, no se muestra esa columna.
   */
  estadosLegajo?: Map<string, string>;
  /**
   * Observaciones reportadas por el titular, agrupadas por asociadoId.
   * Se renderizan en una segunda fila debajo del asociado para que el
   * operador RRHH/Medicus vea lo que el titular marcó como incorrecto.
   */
  observacionesPorAsociado?: Map<string, ObservacionTitular[]>;
  /**
   * Si true, se muestra la columna "Renaper" con el badge del estado por
   * fila. Por requerimiento de producto, esta info SOLO la ve admin Medicus
   * (admin-md / admin-sa). El usuario de empresa no debe saber que se valida
   * contra Renaper; default false para que el componente sea seguro por
   * omisión.
   */
  mostrarRenaper?: boolean;
}

// El estado del legajo se pinta con <EstadoLegajoBadge> (catálogo único en
// lib/estado-legajo.ts) — ya no hay un mapa local de label/color/ícono.

// Mapa visual del estado Renaper por asociado. Cuando admin medicus dispara
// "Validar Renaper" sobre el padrón, cada fila pasa de pendiente a uno de
// los 4 finales (validado / no_coincide / no_encontrado / error).
const RENAPER_ESTADO_UI: Record<
  string,
  { label: string; short: string; color: string; bg: string }
> = {
  pendiente: { label: 'Pendiente Renaper', short: 'Pend.', color: '#737373', bg: '#F3F4F6' },
  validado: { label: 'Renaper OK', short: '✓ OK', color: '#065F46', bg: '#A7F3D0' },
  no_coincide: {
    label: 'No coincide (datos mal)',
    short: '⚠ Mismatch',
    color: '#92400E',
    bg: '#FEF3C7',
  },
  no_encontrado: {
    label: 'No encontrado en Renaper',
    short: '✗ No DNI',
    color: '#B91C1C',
    bg: '#FEE2E2',
  },
  error: { label: 'Error Renaper', short: '! Error', color: '#B91C1C', bg: '#FEE2E2' },
};

const LEGAJO_COLORS = [
  { left: '#0EA5E9', tint: 'rgba(14,165,233,0.04)' }, // azul
  { left: '#10B981', tint: 'rgba(16,185,129,0.04)' }, // verde
  { left: '#F59E0B', tint: 'rgba(245,158,11,0.04)' }, // ámbar
  { left: '#A78BFA', tint: 'rgba(167,139,250,0.04)' }, // violeta
];

function colorForLegajo(numero: string | undefined, idxAlt: number) {
  if (!numero) return LEGAJO_COLORS[idxAlt % LEGAJO_COLORS.length];
  // Hash simple → mismo legajo siempre tiene el mismo color, incluso si hay
  // paginación. Math.imul mantiene el wrap a int32 sin usar `| 0` (sonar S7767).
  let h = 0;
  for (let i = 0; i < numero.length; i++) {
    h = Math.imul(h, 31) + numero.charCodeAt(i);
  }
  return LEGAJO_COLORS[Math.abs(h) % LEGAJO_COLORS.length];
}

export function AsociadosTable({
  asociados,
  onEdit,
  onDelete,
  estadosLegajo,
  observacionesPorAsociado,
  mostrarRenaper = false,
}: Props) {
  // Asociado actualmente abierto en el modal de detalle Renaper. Solo se
  // usa cuando `mostrarRenaper` está activo (admin Medicus).
  const [renaperDetalleAsociado, setRenaperDetalleAsociado] = useState<Asociado | null>(null);

  if (asociados.length === 0) {
    return (
      <p className="text-element-mid" style={{ padding: '1rem' }}>
        Sin registros para mostrar.
      </p>
    );
  }

  const mostrarEstadoLegajo = !!estadosLegajo;

  // Cantidad de columnas reales — necesario para el colSpan del detail row.
  // Estado + ID Web + [Renaper] + [Proceso] + COLUMNAS_VISIBLES + Acciones.
  const totalCols =
    1 +
    1 + // ID Web
    (mostrarRenaper ? 1 : 0) +
    (mostrarEstadoLegajo ? 1 : 0) +
    COLUMNAS_VISIBLES.length +
    1;

  // Indexamos cada legajo según el orden de aparición para que el color
  // alterne de forma estable cuando el legajo es nuevo o no tiene numero.
  const indiceLegajos = new Map<string, number>();
  asociados.forEach((a) => {
    const k = a.legajo ?? `__row_${a.id}`;
    if (!indiceLegajos.has(k)) indiceLegajos.set(k, indiceLegajos.size);
  });

  return (
    <div className="tableWrap">
      <table className="dataTable">
        <thead>
          <tr>
            <th>Estado</th>
            <th>ID Web</th>
            {mostrarRenaper && <th>Renaper</th>}
            {mostrarEstadoLegajo && <th>Proceso</th>}
            {COLUMNAS_VISIBLES.map((c) => (
              <th key={c.key}>{c.header}</th>
            ))}
            <th className="actionsCol">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {asociados.map((a, i) => {
            const esTitular = (a.parentesco ?? '').trim() === '0';
            const legajoKey = a.legajo ?? `__row_${a.id}`;
            const idxAlt = indiceLegajos.get(legajoKey) ?? 0;
            const colors = colorForLegajo(a.legajo, idxAlt);
            const prev = asociados[i - 1];
            const cambiaLegajo = !prev || (prev.legajo ?? `__row_${prev.id}`) !== legajoKey;
            const estadoLegajoKey = a.legajo ? estadosLegajo?.get(a.legajo) : undefined;
            const obs = observacionesPorAsociado?.get(a.id) ?? [];
            const tieneDetalle = a.errores.length > 0 || obs.length > 0;
            return (
              <Fragment key={a.id}>
                <tr
                  className={a.estado === 'error' ? 'rowError' : ''}
                  style={{
                    background: a.estado === 'error' ? undefined : colors.tint,
                    borderTop: cambiaLegajo ? `2px solid ${colors.left}` : undefined,
                    boxShadow: `inset 4px 0 0 0 ${colors.left}`,
                  }}
                >
                  <td>
                    <span
                      className={
                        a.estado === 'ok' ? 'status status-success' : 'status status-error'
                      }
                    >
                      {a.estado === 'ok' ? 'OK' : 'Error'}
                    </span>
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--color-element-mid)' }}>
                    {a.id}
                  </td>

                  {mostrarRenaper && (
                    <td>
                      <RenaperBadge
                        asociado={a}
                        onClick={() => setRenaperDetalleAsociado(a)}
                      />
                    </td>
                  )}

                  {mostrarEstadoLegajo && (
                    <td>
                      {esTitular && estadoLegajoKey ? (
                        <EstadoLegajoBadge estado={estadoLegajoKey} size={12} />
                      ) : esTitular && a.legajo ? (
                        <span style={{ fontSize: 12, color: 'var(--color-element-mid)' }}>—</span>
                      ) : null}
                    </td>
                  )}

                  {COLUMNAS_VISIBLES.map((c) => {
                    const val = (a[c.key] ?? '') as string | number;
                    // Para parentesco mostramos código + label (ej. "3 Hermano")
                    // para que el usuario entienda el dato sin tener que
                    // memorizar el Anexo I.
                    if (c.key === 'parentesco') {
                      const code = String(val).trim();
                      const label = PARENTESCO_LABEL[code];
                      return (
                        <td key={c.key}>
                          {code}
                          {label ? (
                            <span style={{ color: 'var(--color-element-mid)', marginLeft: 4 }}>
                              {label}
                            </span>
                          ) : null}
                        </td>
                      );
                    }
                    // Resaltamos al titular con bold en apellido + ícono.
                    if (c.key === 'apellSolt') {
                      return (
                        <td key={c.key}>
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              fontWeight: esTitular ? 700 : 400,
                              paddingLeft: esTitular ? 0 : 14,
                            }}
                          >
                            {esTitular ? (
                              <UserRound size={14} color={colors.left} />
                            ) : (
                              <Users
                                size={12}
                                color="var(--color-element-mid)"
                                style={{ opacity: 0.6 }}
                              />
                            )}
                            {val}
                          </span>
                        </td>
                      );
                    }
                    return <td key={c.key}>{val}</td>;
                  })}

                  <td className="actionsCol">
                    <div className="actionsCol__group">
                      {onEdit && (
                        <button
                          type="button"
                          className="iconBtn iconBtn--primary"
                          onClick={() => onEdit(a)}
                          aria-label="Editar fila"
                          title="Editar"
                        >
                          <Pencil size={14} />
                          <span className="iconBtn__label">Editar</span>
                        </button>
                      )}
                      {onDelete && (
                        <button
                          type="button"
                          className="iconBtn iconBtn--danger"
                          onClick={() => onDelete(a)}
                          aria-label="Eliminar fila"
                          title="Sacar este registro del padrón"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>

                {tieneDetalle && (
                  <DetailRow asociado={a} observaciones={obs} colSpan={totalCols} />
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {renaperDetalleAsociado && (
        <RenaperDetalleModal
          asociado={renaperDetalleAsociado}
          onClose={() => setRenaperDetalleAsociado(null)}
        />
      )}
    </div>
  );
}

/**
 * Fila secundaria con el detalle de errores u observaciones del titular.
 * Ocupa todo el ancho de la tabla para que el operador no tenga que hacer
 * scroll horizontal — es lo primero que tiene que leer para corregir el
 * padrón. El borde izquierdo pinta el tono según haya errores (rojo) o
 * solo observaciones (ámbar).
 */
function DetailRow({
  asociado,
  observaciones,
  colSpan,
}: {
  asociado: Asociado;
  observaciones: ObservacionTitular[];
  colSpan: number;
}) {
  const soloObs = asociado.errores.length === 0 && observaciones.length > 0;
  return (
    <tr className="detailRow">
      <td colSpan={colSpan}>
        <div
          className={
            soloObs ? 'detailRow__inner detailRow__inner--soloObs' : 'detailRow__inner'
          }
        >
          {asociado.errores.map((e, i) => (
            <div key={`err-${i}`} className="detailRow__error">
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
              <div className="detailRow__msg">
                {e.field ? <span className="detailRow__field">{e.field}: </span> : null}
                {e.message}
                {e.rule === 'unique.cross-padron' && e.meta?.padronOrigenId && (
                  <>
                    {' '}
                    <Link
                      to={`/padrones/${e.meta.padronOrigenId}?highlightLegajo=${e.meta.legajoOrigenId ?? ''}`}
                      style={{ fontWeight: 600, color: 'var(--color-primary-darker)' }}
                    >
                      Ir al padrón origen →
                    </Link>
                  </>
                )}
              </div>
            </div>
          ))}
          {observaciones.map((o, i) => (
            <div key={`obs-${i}`} className="detailRow__obs">
              <Flag size={14} style={{ flexShrink: 0, marginTop: 2 }} />
              <div className="detailRow__msg">
                <span className="detailRow__field">El titular reportó {o.campo}:</span>{' '}
                {o.mensaje}
              </div>
            </div>
          ))}
        </div>
      </td>
    </tr>
  );
}

function RenaperBadge({
  asociado,
  onClick,
}: {
  asociado: Asociado;
  onClick: () => void;
}) {
  const estado = asociado.renaperValidacion?.estado ?? 'pendiente';
  const ui = RENAPER_ESTADO_UI[estado] ?? RENAPER_ESTADO_UI.pendiente;
  const motivo = asociado.renaperValidacion?.motivo;
  const pisosCount = asociado.renaperValidacion?.pisos?.length ?? 0;
  return (
    <button
      type="button"
      onClick={onClick}
      title={motivo ? `${ui.label} — ${motivo} (click para ver detalle)` : `${ui.label} (click para ver detalle)`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        color: ui.color,
        background: ui.bg,
        whiteSpace: 'nowrap',
        border: 'none',
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      {ui.short}
      {pisosCount > 0 && (
        <span
          aria-label={`${pisosCount} campos actualizados`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '0 5px',
            borderRadius: 999,
            background: 'rgba(0,0,0,0.12)',
            fontSize: 10,
            fontWeight: 700,
          }}
        >
          +{pisosCount}
        </span>
      )}
    </button>
  );
}
